// ./context/message/messageReducer.js

const messageReducer = (state, { type, payload }) => {
    switch (type) {
        // ============================================================================
        // FCM TOKEN MANAGEMENT
        // ============================================================================
        case 'SET_USER_TOKEN':
            return {
                ...state,
                userToken: payload,
                isLoading: false,
                error: null
            };

        case 'REMOVE_USER_TOKEN':
            return {
                ...state,
                userToken: null,
                isLoading: false,
                error: null
            };

        case 'SET_REGISTERED_USERS':
            return {
                ...state,
                registeredUsers: payload,
                isLoading: false,
                error: null
            };

        // ============================================================================
        // MESSAGES
        // ============================================================================
        case 'SET_MESSAGES':
            return {
                ...state,
                messages: payload.messages || payload,
                totalMessages: payload.totalMessages || payload.length,
                currentPage: payload.currentPage || 1,
                totalPages: payload.totalPages || 1,
                isLoading: false,
                error: null
            };

        case 'ADD_MESSAGE':
            return {
                ...state,
                messages: [...state.messages, payload],
                lastSentMessage: payload,
                isLoading: false,
                error: null
            };

        case 'UPDATE_MESSAGE':
            return {
                ...state,
                messages: state.messages.map(msg =>
                    msg._id === payload._id || msg.messageId === payload.messageId
                        ? { ...msg, ...payload }
                        : msg
                ),
                isLoading: false,
                error: null
            };

        case 'DELETE_MESSAGE':
            return {
                ...state,
                messages: state.messages.filter(
                    msg => msg._id !== payload && msg.messageId !== payload
                ),
                isLoading: false,
                error: null
            };

        case 'SET_MESSAGE_INFO':
            return {
                ...state,
                messageInfo: payload,
                isLoading: false,
                error: null
            };

        case 'SET_CURRENT_CONVERSATION':
            return {
                ...state,
                currentConversation: payload,
                isLoading: false,
                error: null
            };

        // ============================================================================
        // MESSAGE STATUS
        // ============================================================================
        case 'MARK_MESSAGE_AS_READ':
            return {
                ...state,
                messages: state.messages.map(msg =>
                    msg._id === payload || msg.messageId === payload
                        ? { ...msg, seen: true }
                        : msg
                ),
                isLoading: false,
                error: null
            };

        case 'MARK_ALL_AS_READ':
            return {
                ...state,
                messages: state.messages.map(msg => ({ ...msg, seen: true })),
                unreadCount: 0,
                isLoading: false,
                error: null
            };

        case 'UPDATE_MESSAGE_STATUS':
            return {
                ...state,
                messages: state.messages.map(msg =>
                    msg._id === payload.messageId || msg.messageId === payload.messageId
                        ? { ...msg, deliveryStatus: payload.status }
                        : msg
                ),
                isLoading: false,
                error: null
            };

        case 'UPDATE_MULTIPLE_MESSAGE_STATUS':
            return {
                ...state,
                messages: state.messages.map(msg =>
                    payload.messageIds.includes(msg._id) || payload.messageIds.includes(msg.messageId)
                        ? { ...msg, deliveryStatus: payload.status }
                        : msg
                ),
                isLoading: false,
                error: null
            };

        // ============================================================================
        // UNREAD COUNTS
        // ============================================================================
        case 'SET_UNREAD_COUNT':
            return {
                ...state,
                unreadCount: payload,
                isLoading: false,
                error: null
            };

        case 'SET_USERS_WITH_UNREAD_COUNTS':
            return {
                ...state,
                usersWithUnreadCounts: payload.users || payload,
                totalUnreadCount: payload.totalUnreadCount || 0,
                usersWithUnreadMessages: payload.usersWithUnreadMessages || [],
                countsBySender: payload.countsBySender || {},
                isLoading: false,
                error: null
            };

        case 'INCREMENT_UNREAD_COUNT':
            return {
                ...state,
                unreadCount: state.unreadCount + (payload || 1),
                isLoading: false,
                error: null
            };

        case 'DECREMENT_UNREAD_COUNT':
            return {
                ...state,
                unreadCount: Math.max(0, state.unreadCount - (payload || 1)),
                isLoading: false,
                error: null
            };

        case 'RESET_UNREAD_COUNT':
            return {
                ...state,
                unreadCount: 0,
                isLoading: false,
                error: null
            };

        // ============================================================================
        // MULTICAST
        // ============================================================================
        case 'SET_MULTICAST_RESULT':
            return {
                ...state,
                lastMulticastResult: payload,
                isLoading: false,
                error: null
            };

        // ============================================================================
        // ADMIN FUNCTIONS
        // ============================================================================
        case 'SET_REGISTERED_TOKENS_INFO':
            return {
                ...state,
                registeredTokensInfo: payload,
                isLoading: false,
                error: null
            };

        case 'SET_VALIDATION_RESULTS':
            return {
                ...state,
                validationResults: payload,
                isLoading: false,
                error: null
            };

        case 'SET_TEST_RESULTS':
            return {
                ...state,
                testResults: payload,
                isLoading: false,
                error: null
            };

        case 'SYNC_TOKENS_SUCCESS':
            return {
                ...state,
                registeredTokensInfo: {
                    ...state.registeredTokensInfo,
                    tokensLoaded: payload.tokensLoaded
                },
                isLoading: false,
                error: null
            };

        // ============================================================================
        // UTILITY
        // ============================================================================
        case 'SET_LOADING':
            return {
                ...state,
                isLoading: payload
            };

        case 'SET_ERROR':
            return {
                ...state,
                error: payload,
                isLoading: false
            };

        case 'CLEAR_ERROR':
            return {
                ...state,
                error: null
            };

        case 'CLEAR_MESSAGE_DATA':
            return {
                registeredUsers: [],
                userToken: null,
                messages: [],
                currentConversation: null,
                messageInfo: null,
                unreadCount: 0,
                usersWithUnreadCounts: [],
                totalUnreadCount: 0,
                countsBySender: {},
                usersWithUnreadMessages: [],
                totalMessages: 0,
                currentPage: 1,
                totalPages: 1,
                registeredTokensInfo: null,
                validationResults: null,
                testResults: null,
                lastMulticastResult: null,
                isLoading: false,
                error: null,
                lastSentMessage: null
            };

        default:
            return state;
    }
};

export default messageReducer;